import IncidentCaseV2 from '@features/logs/components/incidents/v2/IncidentCaseV2'
import useIncidentCaseV2 from '@features/logs/components/incidents/v2/useIncidentCaseV2'
import {
  isV2Case,
  usesV1Form
} from '@features/logs/components/incidents/v2/usesV1Form'
import { useIncidentListReturn } from '@features/logs/hooks/useIncidentListReturn'
import { Button } from '@lmc/design-system'
import { ConnectionIcon, EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { Spinner } from 'components/generic'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

type IncidentCaseRouteProps = {
  /** The v1 screen this route shows when the case belongs to v1. */
  v1Form: ReactNode
  testId?: string
}

/**
 * The v1 or v2 case form. A case v2 opened always opens in v2; otherwise the
 * flag decides, and neither shows while it loads.
 */
const IncidentCaseRoute = ({
  v1Form,
  testId = 'incident-case-route'
}: IncidentCaseRouteProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { navigateWithinIncident } = useIncidentListReturn()
  const { id: incidentLogId } = useParams()
  const { isEnabled, isLoading: areFlagsLoading } = useIncidentCaseV2()

  const { incidentReport, isError, isIncidentReportLoading, refetch } =
    useIncidentReportByLog({ incidentLogId })

  // The case's version is only known once the report has loaded.
  if (isIncidentReportLoading) {
    return <Spinner data-testid={`${testId}-spinner`} />
  }

  const exitToReport = () => navigateWithinIncident('../')

  if (incidentReport && isV2Case(incidentReport)) {
    return (
      <IncidentCaseV2 incidentReport={incidentReport} onExit={exitToReport} />
    )
  }

  if (areFlagsLoading) {
    return <Spinner data-testid={`${testId}-flags-spinner`} />
  }

  if (!isEnabled) {
    return <>{v1Form}</>
  }

  // A failed request is not a missing log.
  if (!incidentReport && isError) {
    return (
      <EmptyState
        testId={`${testId}-error-state`}
        icon={<ConnectionIcon />}
        title={t(incidentReportsConstants.CASE_V2_CASE_LOAD_FAILED)}
      >
        <Button
          variant="secondary"
          data-testid={`${testId}-retry-button`}
          onClick={() => refetch()}
        >
          {t(incidentReportsConstants.CASE_V2_TRY_AGAIN)}
        </Button>
      </EmptyState>
    )
  }

  if (!incidentReport) {
    return (
      <EmptyState
        testId={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )
  }

  // A case the v1 form opened stays in the v1 form; anything else is v2's.
  if (usesV1Form(incidentReport)) {
    return <>{v1Form}</>
  }

  return (
    <IncidentCaseV2 incidentReport={incidentReport} onExit={exitToReport} />
  )
}

export default IncidentCaseRoute
